import { useCallback, useEffect, useRef, useState } from "react";
import {
  Procedure,
  ProcedureSearch
} from "@kenettotech/scribo-shared-functions";
import { OCCUPATION } from "../constants";

/** Loaded sets are kept around rather than fetched and parsed again. */
const cache = new Map<string, ProcedureSearch>();

/**
 * Loads a prebuilt index and the searcher over it.
 *
 * Both come from packages the mobile app shares, so neither repository carries
 * its own copy: `scribo-shared-data` holds the nomenclature and its serialised
 * lunr index, and `scribo-shared-functions` holds the search itself — the
 * accent folding, the weighted query and the relevance cut-off, which have to
 * stay identical across the two apps or the same search starts ranking
 * differently on each.
 *
 * Everything is imported dynamically: that is what keeps the sets — the doctor
 * one is far too large to ship on first paint — and lunr out of the initial
 * bundle. They are fetched only once the procedure selector is opened.
 */
async function loadSearch(directory: string): Promise<ProcedureSearch> {
  const cached = cache.get(directory);
  if (cached) return cached;

  const [{ createProcedureSearch }, dataset] = await Promise.all([
    import("@kenettotech/scribo-shared-functions"),
    import(
      "@kenettotech/scribo-shared-data/async"
    ).then(({ loadPaymentsDoctor, loadPaymentsOthers }) =>
      directory === "doctor" ? loadPaymentsDoctor() : loadPaymentsOthers()
    )
  ]);

  const search = createProcedureSearch(dataset);
  cache.set(directory, search);
  return search;
}

/**
 * Full text search over the medical procedure nomenclature.
 *
 * Which set applies depends on the professional's occupation, and both are
 * shipped as prebuilt Lunr indexes rather than queried from the API.
 */
export default function useProcedureSearch(occupation?: number) {
  const directory = occupation === OCCUPATION.DOCTOR ? "doctor" : "others";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const searchRef = useRef<ProcedureSearch | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadSearch(directory)
      .then(loaded => {
        if (!active) return;
        searchRef.current = loaded;
        setError(null);
      })
      .catch(err => active && setError(err as Error))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [directory]);

  /** Empty until the set has loaded, rather than throwing on an early keypress. */
  const search = useCallback(
    (query: string): Procedure[] => searchRef.current?.search(query) ?? [],
    []
  );

  return { search, loading, error };
}
